import React from 'react';
import { toMonths } from '../../modules/motorMilestones/motorMilestones';

/**
 * Janelas de conquista da OMS em SVG puro (só traços e texto, sem imagem),
 * para sair igual na tela e no PDF (html2canvas): uma barra por marco do
 * percentil 1 ao 99, faixa mais forte do 3 ao 97, traço na mediana, ponto
 * na idade da conquista e linha vertical na idade atual.
 *
 * `rows`: saída de milestoneRows (só os marcos com janela entram);
 * `current`: idade atual (ageAt) ou null; `corrected`: eixo em idade corrigida.
 */

const WIDTH = 640;
const LABEL_WIDTH = 150;
const MARGIN = { top: 10, right: 18, bottom: 36 };
const ROW_HEIGHT = 30;

const COLORS = {
  outer: '#E6FFFA',
  inner: '#81E6D9',
  median: '#2C7A7B',
  grid: '#EDF2F7',
  text: '#718096',
  label: '#2D3748',
  current: '#805AD5',
  ok: '#38A169',
  info: '#5A80FB',
  alert: '#DD6B20',
};

const MotorMilestonesChart = ({ rows, current, corrected = false, currentLabel = 'Hoje' }) => {
  const windowed = rows.filter((row) => row.milestone.window);
  const achievedMonths = windowed.filter((row) => row.age).map((row) => toMonths(row.age.days));
  const currentMonths = current ? toMonths(current.days) : null;
  const maxMonths = Math.max(20, ...achievedMonths, currentMonths != null && currentMonths <= 30 ? currentMonths : 0);
  const to = Math.ceil(maxMonths / 2) * 2;

  const height = MARGIN.top + windowed.length * ROW_HEIGHT + MARGIN.bottom;
  const plotLeft = LABEL_WIDTH;
  const plotWidth = WIDTH - plotLeft - MARGIN.right;
  const plotBottom = MARGIN.top + windowed.length * ROW_HEIGHT;
  const sx = (months) => plotLeft + (Math.min(months, to) / to) * plotWidth;
  const sd = (days) => sx(toMonths(days));

  const ticks = [];
  for (let month = 0; month <= to; month += 2) ticks.push(month);

  return (
    <svg viewBox={`0 0 ${WIDTH} ${height}`} width="100%" role="img" aria-label="Janelas de conquista dos marcos motores" style={{ display: 'block' }}>
      {ticks.map((tick) => (
        <g key={`x${tick}`}>
          <line x1={sx(tick)} x2={sx(tick)} y1={MARGIN.top} y2={plotBottom} stroke={COLORS.grid} strokeWidth="1" />
          <text x={sx(tick)} y={plotBottom + 14} textAnchor="middle" fontSize="10" fill={COLORS.text}>{tick}</text>
        </g>
      ))}
      <text x={plotLeft + plotWidth / 2} y={height - 6} textAnchor="middle" fontSize="10.5" fill={COLORS.text}>
        {corrected ? 'Idade corrigida (meses)' : 'Idade (meses)'}
      </text>

      {windowed.map((row, index) => {
        const { window } = row.milestone;
        const cy = MARGIN.top + index * ROW_HEIGHT + ROW_HEIGHT / 2;
        return (
          <g key={row.milestone.key}>
            <text x={plotLeft - 10} y={cy + 4} textAnchor="end" fontSize="11" fill={COLORS.label}>{row.milestone.label}</text>
            <rect x={sd(window.p1)} y={cy - 7} width={sd(window.p99) - sd(window.p1)} height="14" rx="7" fill={COLORS.outer} stroke={COLORS.inner} strokeWidth="0.8" />
            <rect x={sd(window.p3)} y={cy - 4} width={sd(window.p97) - sd(window.p3)} height="8" rx="4" fill={COLORS.inner} />
            <line x1={sd(window.p50)} x2={sd(window.p50)} y1={cy - 8} y2={cy + 8} stroke={COLORS.median} strokeWidth="2" />
            {row.age && (
              <circle cx={sd(row.age.days)} cy={cy} r="5" fill={COLORS[row.status.tone] || COLORS.ok} stroke="#FFFFFF" strokeWidth="1.5" />
            )}
          </g>
        );
      })}

      {currentMonths != null && currentMonths <= to && (
        <g>
          <line x1={sx(currentMonths)} x2={sx(currentMonths)} y1={MARGIN.top - 4} y2={plotBottom} stroke={COLORS.current} strokeWidth="1.5" strokeDasharray="4 3" />
          <text x={sx(currentMonths) + 4} y={plotBottom - 4} fontSize="10" fontWeight="600" fill={COLORS.current}>{currentLabel}</text>
        </g>
      )}
    </svg>
  );
};

export default MotorMilestonesChart;
